import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import { useIsClickToEditEnabled } from "../../contexts/StaticSiteDataContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import { TRACKING } from "../../core/components/LaunchDarklyProvider";
import {
  CashRegistryDetails,
  Configuration,
  OnlineInvitationSiteDetails,
  Section,
  SectionTypes,
  Site,
  WebsiteEvent,
} from "../../resources/modelTypes";
import { Page } from "../../resources/modelTypes";
import { SelectedCollection, SelectedRecord } from "../../resources/selectors";
import { useScrollTo } from "../../shared/hooks/useScrollTo";
import SitePreviewContext from "../../website/components/SitePreviewContext";
import { sectionTypeToSwipePanelView } from "../../website/components/online-invitation/mobile/SectionHeights";
import { sortRecordsByCreatedAtAndPosition } from "../../website/utils";
import { SiteDetailsSections } from "../collapsed/CollapsedSiteDetails";
import { selectors } from "../cypressSelectors";
import { PageSlugs } from "../types";
import { isEventDetailsEmpty } from "../utils";
import { DisplayEvent } from "./DisplayEvent";
import { DisplayRSVP } from "./DisplayRSVP";
import { DisplaySectionWebsite } from "./DisplaySection";
import { DisplaySiteDetails } from "./DisplaySiteDetails";
import { DisplayWrapper } from "./DisplayWrapper";
import {
  AttendantSection,
  DisplayAttendantSections,
} from "./section-types/DisplayAttendantSection";
import { DisplayDividerSection } from "./section-types/DisplayDividerSection";
import { DisplayMap } from "./section-types/DisplayMap";
import RegistriesGrid, {
  GroupedRegistriesSection,
} from "./section-types/RegistriesGrid";
import { css, SerializedStyles } from "@emotion/react";
import { MediumHeadline } from "@minted/fancyclothes";
import {
  PillButton,
  styleUtils,
  theme,
  useMediaQueryState,
  usePointerState,
  IconAdd,
  IconEditSquare,
} from "@minted/minted-components";
import React, { Fragment, memo, useCallback, useContext, useMemo } from "react";

export const dashedBorderStyle = css`
  border: ${styleUtils.rem(2)} dashed ${theme.colors.white};
  position: relative;

  &::before {
    border: ${styleUtils.rem(2)} dashed ${theme.colors.black};
    bottom: -${styleUtils.rem(4)};
    content: "";
    left: -${styleUtils.rem(4)};
    pointer-events: none;
    position: absolute;
    right: -${styleUtils.rem(4)};
    top: -${styleUtils.rem(4)};
  }
`;

export const displayWebsiteContentStyles = {
  addNewSectionContainer: css`
    align-items: center;
    background-color: rgba(255, 255, 255, 0.51);
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacing.x10};
    padding: ${theme.spacing.x10};
  `,
  focusedSectionSolid: css`
    border: ${styleUtils.rem(2)} solid ${theme.colors.white};
    position: relative;

    &::before {
      border: ${styleUtils.rem(2)} solid ${theme.colors.borderFocused};
      bottom: -${styleUtils.rem(4)};
      content: "";
      left: -${styleUtils.rem(4)};
      pointer-events: none;
      position: absolute;
      right: -${styleUtils.rem(4)};
      top: -${styleUtils.rem(4)};
    }
  `,
  oiEventDetailsHeader: css`
    display: grid;
    justify-content: center;
    margin-bottom: ${theme.spacing.x4};
    text-align: center;
  `,
  oiRegistryHeader: css`
    margin-top: ${theme.spacing.x4};
  `,
  pillButton: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x2};
  `,
  topPadding: css`
    padding-top: ${theme.spacing.x2};
  `,
};

export enum SectionEditState {
  /** No border and no edit pill. */
  Hidden = "hidden",
  /** Dashed border plus edit pill — hovered on desktop, tapped once on mobile. */
  Revealed = "revealed",
  /** Solid border and no edit pill — this section's editor is open. */
  Editing = "editing",
}

export type SectionEditStateParams = {
  isAlwaysRevealed?: boolean;
  isClickable: boolean;
  isFocused: boolean;
  isHovered: boolean;
  isSolidBorder: boolean;
};

/**
 * Resolves how a section should present its edit controls, from the preview's
 * hover and focus state.
 *
 * Desktop reveals on hover and opens the editor on the first click, so a section
 * goes straight from `Hidden` to `Editing`. Mobile has no hover, so the first tap
 * focuses the section and the second opens its editor.
 */
export const getSectionEditState = ({
  isAlwaysRevealed = false,
  isClickable,
  isFocused,
  isHovered,
  isSolidBorder,
}: SectionEditStateParams): SectionEditState => {
  if (isFocused) {
    return isSolidBorder ? SectionEditState.Editing : SectionEditState.Revealed;
  }

  if (isAlwaysRevealed || (isClickable && isHovered)) {
    return SectionEditState.Revealed;
  }

  return SectionEditState.Hidden;
};

/**
 * Maps a section's edit state to the border style that represents it.
 */
export const getEditStateBorderStyle = (
  editState: SectionEditState,
): SerializedStyles | undefined => {
  switch (editState) {
    case SectionEditState.Editing:
      return displayWebsiteContentStyles.focusedSectionSolid;
    case SectionEditState.Revealed:
      return dashedBorderStyle;
    case SectionEditState.Hidden:
      return undefined;
  }
};

export type DisplayWebsiteContentProps = {
  configuration: Configuration;
  eventCollection: SelectedCollection<WebsiteEvent> | null;
  page: Page;
  sectionCollection: SelectedCollection<Section>;
  site: Site;
};

type Sections = (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];

function getItemsWithGroupedRegistries(items: Sections) {
  let registryGroup: Sections = [];

  const itemsWithGroupedRegistries = items.reduce(
    (
      nextItems: Sections,
      section: SelectedRecord<WebsiteEvent> | SelectedRecord<Section>,
      index: number,
      arr: Sections,
    ): Sections => {
      const sectionData = section.data as Section;
      const sectionType: SectionTypes = sectionData.sectionType;
      const isLastItem = index === arr.length - 1;
      const isBirdieCashRegistry =
        sectionType === SectionTypes.CashRegistry &&
        Boolean(
          (sectionData.sectionDetails as CashRegistryDetails)?.cashRegistryId,
        );

      if (
        !isBirdieCashRegistry &&
        (sectionType === SectionTypes.Registry ||
          sectionType === SectionTypes.CashRegistry ||
          sectionType === SectionTypes.StripeRegistry)
      ) {
        registryGroup = [...registryGroup, section as SelectedRecord<Section>];

        if (isLastItem) {
          return [
            ...nextItems,
            {
              data: {
                sectionType: SectionTypes.RegistryGroup,
                registries: registryGroup,
              },
            } as unknown as SelectedRecord<Section>,
          ];
        }

        return [...nextItems];
      }

      const items = [
        ...(nextItems as Sections),
        ...(registryGroup.length > 0
          ? [
              {
                data: {
                  sectionType: SectionTypes.RegistryGroup,
                  registries: registryGroup,
                },
              } as unknown as SelectedRecord<Section>,
            ]
          : []),
        section as SelectedRecord<WebsiteEvent> | SelectedRecord<Section>,
      ];

      registryGroup = [];

      return items;
    },
    [] as (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[],
  );

  return itemsWithGroupedRegistries;
}

/**
 * Whether the item after `index` is a user-added divider section, in which case
 * an automatic divider before it would render two dividers in a row.
 */
const isNextItemDividerSection = (
  items: (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[],
  index: number,
): boolean => {
  const nextItem = items[index + 1]?.data;
  return (
    nextItem !== undefined &&
    "sectionType" in nextItem &&
    nextItem.sectionType === SectionTypes.Divider
  );
};

const DisplayWebsiteContent = ({
  configuration,
  eventCollection,
  page,
  sectionCollection,
  site,
}: DisplayWebsiteContentProps) => {
  const { isPreview } = useIsPreviewOrDemo();
  const currentSite = useCurrentSite();
  const { isOnlineInvitation, isWedding } = currentSite;
  const isClickToEditEnabled = useIsClickToEditEnabled();

  const stagedSection = sectionCollection.resource.staged?.create?.[0];
  const stagedPageId = stagedSection?.data?.page;
  const sectionCollectionPageId = sectionCollection.params.page;
  const stagedEvent = eventCollection?.resource.staged?.create[0];
  const eventPageName = eventCollection?.resource?.name;
  const hasStagedCreation = stagedSection || stagedEvent;
  const {
    focusedClickSection,
    hoveredSection,
    onSectionEditClick,
    setFocusedClickSection,
    setHoveredSection,
    triggerAddNewSection,
  } = useContext(SitePreviewContext);

  const { scrollToId } = useScrollTo();

  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });
  const isDesktop = smallMediaQueryState !== "BELOW";

  const isPreviewClickable = useMemo(
    () => isPreview && isClickToEditEnabled,
    [isPreview, isClickToEditEnabled],
  );

  // Desktop reveals a section's border and edit pill on hover, so a click can go
  // straight to the editor.
  //
  // Touch devices have no hover, so the first tap reveals and the second opens the
  // editor.
  const isTapToReveal = usePointerState() === "TOUCH" || !isDesktop;

  const shouldTrackHover = isPreviewClickable && !isTapToReveal;

  /**
   * Reveals a section on the first click and opens its editor on the second.
   *
   * @param skipReveal - Collapse both stages into a single click, for cases where
   * the border and edit pill are already visible (desktop hover, or an empty
   * event).
   */
  const handleSectionClick = useCallback(
    (
      idOfResource: number | SiteDetailsSections,
      resourceType: "event" | "section" | "siteDetails",
      sectionType: SectionTypes,
      skipReveal: boolean = false,
    ) => {
      const isAlreadyRevealed =
        focusedClickSection?.id === idOfResource &&
        focusedClickSection?.type === resourceType;

      if (isAlreadyRevealed || skipReveal) {
        setFocusedClickSection({
          id: idOfResource,
          isSolidBorder: true,
          type: resourceType,
        });

        const panelView = sectionTypeToSwipePanelView(sectionType);
        // Pass the page so the sidebar knows which page context we're in
        onSectionEditClick(idOfResource, resourceType, panelView, page);
        scrollToId(`section-${idOfResource}`);
      } else {
        setFocusedClickSection({
          id: idOfResource,
          isSolidBorder: false,
          type: resourceType,
        });
      }
    },
    [
      onSectionEditClick,
      focusedClickSection,
      page,
      scrollToId,
      setFocusedClickSection,
    ],
  );

  const allItems = useMemo(() => {
    let items: (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];

    if (eventCollection?.items) {
      items = [...eventCollection.items, ...sectionCollection.items];

      if (stagedEvent && eventPageName === PageSlugs.Events) {
        items.push(stagedEvent);
      }
    } else {
      items = sectionCollection.items;
    }

    if (stagedSection && stagedPageId === sectionCollectionPageId) {
      items.push(stagedSection);
    }

    sortRecordsByCreatedAtAndPosition(items);

    return getItemsWithGroupedRegistries(items);
  }, [
    eventCollection,
    sectionCollection.items,
    stagedEvent,
    eventPageName,
    stagedSection,
    stagedPageId,
    sectionCollectionPageId,
  ]);

  const extraComponentsForSlug = useMemo(() => {
    if (page.slug === PageSlugs.RSVP) {
      return <DisplayRSVP configuration={configuration} />;
    }

    return null;
  }, [configuration, page.slug]);

  const sectionElements: React.ReactNode[] = useMemo(() => {
    const elements: React.ReactNode[] = [];

    // Render site details for OI sites at the top
    if (isOnlineInvitation && site.siteDetails) {
      const siteDetails =
        site.siteDetails as unknown as OnlineInvitationSiteDetails;
      const isSiteDetailsFocused =
        focusedClickSection?.id ===
          SiteDetailsSections.EventNameAndDescription &&
        focusedClickSection?.type === "siteDetails";

      const isSiteDetailsHovered =
        hoveredSection?.id === SiteDetailsSections.EventNameAndDescription &&
        hoveredSection?.type === "siteDetails";

      const siteDetailsEditState = getSectionEditState({
        isClickable: isPreviewClickable,
        isFocused: isSiteDetailsFocused,
        isHovered: isSiteDetailsHovered,
        isSolidBorder: Boolean(focusedClickSection?.isSolidBorder),
      });

      let siteDetailsBorderStyle: SerializedStyles | undefined;
      let siteDetailsClickHandler: (() => void) | undefined;

      // Assign relevant styling/handler if new preview experience is enabled
      if (isPreview) {
        siteDetailsBorderStyle = getEditStateBorderStyle(siteDetailsEditState);

        if (!hasStagedCreation && isPreviewClickable) {
          siteDetailsClickHandler = () =>
            handleSectionClick(
              SiteDetailsSections.EventNameAndDescription,
              "siteDetails",
              SectionTypes.Text,
              !isTapToReveal,
            );
        }
      }

      const shouldDisplayPillButton =
        isPreview && siteDetailsEditState === SectionEditState.Revealed;

      elements.push(
        <Fragment key="siteDetails">
          <div
            onMouseEnter={
              shouldTrackHover
                ? () =>
                    setHoveredSection({
                      id: SiteDetailsSections.EventNameAndDescription,
                      type: "siteDetails",
                    })
                : undefined
            }
            onMouseLeave={
              shouldTrackHover ? () => setHoveredSection(null) : undefined
            }
          >
            {shouldDisplayPillButton && (
              <div css={displayWebsiteContentStyles.pillButton}>
                <PillButton
                  iconAlignment="right"
                  icon={IconEditSquare}
                  onClick={siteDetailsClickHandler}
                  size="small"
                />
              </div>
            )}
            <div
              css={[
                displayWebsiteContentStyles.topPadding,
                siteDetailsBorderStyle,
              ]}
              data-cy={`${selectors.website_displaySection}_siteDetails`}
              id={`section-${SiteDetailsSections.EventNameAndDescription}`}
              onClick={siteDetailsClickHandler}
            >
              <DisplaySiteDetails siteDetails={siteDetails} />
            </div>
          </div>
        </Fragment>,
      );
    }

    for (let index = 0; index < allItems.length; index++) {
      const current = allItems[index] as
        SelectedRecord<WebsiteEvent> | SelectedRecord<Section>;
      const isEvent = !("sectionType" in current.data);

      if (isEvent) {
        const event = current.data as WebsiteEvent;

        if (event.private) {
          continue;
        }

        const isEventEditFocused =
          focusedClickSection?.id === event.id &&
          focusedClickSection?.type === "event";

        const isEventHovered =
          hoveredSection?.id === event.id && hoveredSection?.type === "event";

        // An empty event is a call to action, so it stays revealed at all times.
        const isEventEmpty = isEventDetailsEmpty(event);

        const eventEditState = getSectionEditState({
          isAlwaysRevealed: isEventEmpty,
          isClickable: isPreviewClickable,
          isFocused: isEventEditFocused,
          isHovered: isEventHovered,
          isSolidBorder: Boolean(focusedClickSection?.isSolidBorder),
        });

        const eventBorderStyle = getEditStateBorderStyle(eventEditState);

        const shouldDisplayPillButton =
          isPreview && eventEditState === SectionEditState.Revealed;

        // An always-revealed event has nothing for a first tap to reveal, so it
        // opens its editor on a single tap.
        const eventClickHandler =
          isPreview && !hasStagedCreation && isPreviewClickable
            ? () =>
                handleSectionClick(
                  event.id,
                  "event",
                  SectionTypes.Event__FAKE,
                  isEventEmpty || !isTapToReveal,
                )
            : undefined;

        elements.push(
          <Fragment key={`collapsed${event.id}`}>
            <div
              onMouseEnter={
                shouldTrackHover
                  ? () => setHoveredSection({ id: event.id, type: "event" })
                  : undefined
              }
              onMouseLeave={
                shouldTrackHover ? () => setHoveredSection(null) : undefined
              }
            >
              {shouldDisplayPillButton && (
                <div css={displayWebsiteContentStyles.pillButton}>
                  <PillButton
                    iconAlignment="right"
                    icon={IconEditSquare}
                    onClick={eventClickHandler}
                    size="small"
                  />
                </div>
              )}
              <div
                css={isPreview && eventBorderStyle}
                data-cy={`${selectors.website_displaySection}_event`}
                id={`section-${event.id || "new"}`}
                onClick={eventClickHandler}
              >
                {isOnlineInvitation && (
                  <div css={displayWebsiteContentStyles.oiEventDetailsHeader}>
                    <MediumHeadline>Event Details</MediumHeadline>
                  </div>
                )}
                <DisplayEvent configuration={configuration} event={event} />
                {configuration.showPageDividers &&
                  !isNextItemDividerSection(allItems, index) && (
                    <DisplayDividerSection
                      hideLast={index === allItems.length - 1}
                    />
                  )}
              </div>
            </div>
          </Fragment>,
        );
        continue;
      }

      const section = current.data as Section;

      if (section.sectionType === SectionTypes.Attendant) {
        const attendantGroup = [section] as AttendantSection[];

        const nextSection = allItems[index + 1]?.data as AttendantSection;

        // If next section is also an Attendant, group them for potential grid display
        if (nextSection && nextSection.sectionType === SectionTypes.Attendant) {
          attendantGroup.push(nextSection);
          index++;
        }

        elements.push(
          <DisplayAttendantSections
            configuration={configuration}
            key={attendantGroup[0].id}
            page={page}
            sections={attendantGroup}
            classes={{
              dashedBorderStyle,
              focusedSectionSolid:
                displayWebsiteContentStyles.focusedSectionSolid,
            }}
          />,
        );
        continue;
      }

      const shouldDisplayDivider =
        configuration.showPageDividers &&
        !isNextItemDividerSection(allItems, index) &&
        [
          SectionTypes.Accommodation,
          SectionTypes.Registry,
          SectionTypes.CashRegistry,
        ].includes(section.sectionType);

      const isSectionFocused =
        focusedClickSection?.id === section.id &&
        focusedClickSection?.type === "section";

      const isSectionHovered =
        hoveredSection?.id === section.id && hoveredSection?.type === "section";

      const isSectionEditable =
        section.sectionType !== SectionTypes.Comments &&
        section.sectionType !== SectionTypes.Divider;

      const sectionEditState = getSectionEditState({
        isClickable: isPreviewClickable,
        isFocused: isSectionFocused,
        isHovered: isSectionHovered,
        isSolidBorder: Boolean(focusedClickSection?.isSolidBorder),
      });

      let sectionBorderStyle: SerializedStyles | undefined;
      let sectionClickHandler: (() => void) | undefined;

      // Assign relevant styling/handler if new preview experience is enabled
      if (isPreview && isSectionEditable) {
        // A section still being created is always shown as the one being edited.
        sectionBorderStyle =
          section.id === undefined
            ? displayWebsiteContentStyles.focusedSectionSolid
            : getEditStateBorderStyle(sectionEditState);

        if (!hasStagedCreation && isPreviewClickable) {
          sectionClickHandler = () =>
            handleSectionClick(
              section.id,
              "section",
              section.sectionType,
              !isTapToReveal,
            );
        }
      }

      const shouldDisplayPillButton =
        isPreview &&
        isSectionEditable &&
        sectionEditState === SectionEditState.Revealed;

      if (section.sectionType === SectionTypes.RegistryGroup) {
        elements.push(
          <RegistriesGrid
            configuration={configuration}
            key={section.id}
            isPreviewClickable={!hasStagedCreation && isPreviewClickable}
            section={section as GroupedRegistriesSection}
          />,
        );
        continue;
      }

      elements.push(
        <Fragment key={`collapsed${section.id}`}>
          <div
            onMouseEnter={
              shouldTrackHover && isSectionEditable
                ? () => setHoveredSection({ id: section.id, type: "section" })
                : undefined
            }
            onMouseLeave={
              shouldTrackHover && isSectionEditable
                ? () => setHoveredSection(null)
                : undefined
            }
          >
            {shouldDisplayPillButton && (
              <div css={displayWebsiteContentStyles.pillButton}>
                <PillButton
                  iconAlignment="right"
                  icon={IconEditSquare}
                  onClick={sectionClickHandler}
                  size="small"
                />
              </div>
            )}
            <div
              css={sectionBorderStyle}
              data-cy={`${selectors.website_displaySection}_${section.sectionType}`}
              id={`section-${section.id || "new"}`}
              onClick={sectionClickHandler}
            >
              <DisplaySectionWebsite
                configuration={configuration}
                section={section}
              />
            </div>
          </div>
          {shouldDisplayDivider && <DisplayDividerSection hideLast />}
        </Fragment>,
      );
    }

    if (isPreview && isPreviewClickable) {
      elements.push(
        <div
          css={[
            displayWebsiteContentStyles.addNewSectionContainer,
            dashedBorderStyle,
          ]}
          key="add-new-section"
        >
          <PillButton
            iconAlignment="right"
            icon={IconAdd}
            onClick={() => {
              triggerAddNewSection(
                TRACKING.PreviewSectionAddElementClicked,
                page,
                isWedding,
              );
            }}
            size="medium"
            text="Add New Section"
          />
        </div>,
      );
    }

    return elements;
  }, [
    allItems,
    configuration,
    focusedClickSection,
    handleSectionClick,
    hasStagedCreation,
    hoveredSection,
    isPreviewClickable,
    isOnlineInvitation,
    isPreview,
    isTapToReveal,
    isWedding,
    page,
    setHoveredSection,
    shouldTrackHover,
    site,
    triggerAddNewSection,
  ]);

  return (
    <DisplayWrapper
      isMobile={!isDesktop}
      textAlignment={configuration.textAlignment}
    >
      {extraComponentsForSlug}
      {sectionElements}
      {isWedding &&
        page.slug === PageSlugs.Events &&
        configuration.showMap &&
        eventCollection && <DisplayMap events={eventCollection.items} />}
    </DisplayWrapper>
  );
};

const MemoizedDisplayWebsiteContent = memo(DisplayWebsiteContent);

export { MemoizedDisplayWebsiteContent as DisplayWebsiteContent };
